// Daily Production Entry (form 5582) — shared by Factory logins (Production.js)
// and GMT Factory inside Dashboard (FactoryView -> DailyProductionScreen).
//
// Clean Modern ERP UI:
//   - Clean header with search, factory selector, and count
//   - Beautiful Light-Blue ERP Data Table with sticky header
//   - Filter row with clean white inputs + search icon, clear button
//   - One-click row opening (hover states + visual pointer)
//   - Badge indicators for Editable vs Locked
//   - Summary footer cards with totals
//
import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../App';
import { CostSheetScreen, FabInhouseScreen, FactoryView } from './Dashboard';
import GmtTrainingModal from './GmtTrainingModal';
import ExportBar from '../components/ExportBar';

const C = (key, label, type = 'text', total = false) => ({ key, label, type, total });

const LINE_COLS = [
  C('entryno', 'Entry No'),
  C('entry_dt_str', 'Date', 'date'),
  C('factory', 'Factory'),
  C('tasno', 'TAS No'),
  C('style_name', 'Style'),
  C('shade', 'Shade'),
  C('order_qty', 'Order Qty', 'int', true),
  C('cuts', "Today's Cut", 'int', true),
  C('tcuts', 'Total Cut', 'int', true),
  C('prodiss', 'Today Issue', 'int', true),
  C('input', 'Total Input', 'int', true),
  C('prod', 'Today Prod', 'int', true),
  C('output', 'Total Output', 'int', true),
  C('tready', 'Today Ready', 'int', true),
  C('ready', 'Ready Qty', 'int', true),
  { key: 'status', label: 'Status', type: 'text', get: r => r.editable ? 'Editable' : 'Locked' },
];

const ENTRY_COLS = [
  C('entryno', 'Entry No'),
  C('entry_date_str', 'Date', 'date'),
  C('factory_name', 'Factory'),
  C('line_count', 'Rows', 'int', true),
  C('tas_list', 'TAS Orders'),
  C('total_order_qty', 'Order Qty', 'int', true),
  C('total_cuts', "Today's Cut", 'int', true),
  C('total_tcuts', 'Total Cut', 'int', true),
  C('total_prodiss', 'Today Issue', 'int', true),
  C('total_input', 'Total Input', 'int', true),
  C('total_prod', 'Today Prod', 'int', true),
  C('total_output', 'Total Output', 'int', true),
  C('total_tready', 'Today Ready', 'int', true),
  C('total_ready', 'Ready Qty', 'int', true),
  { key: 'status', label: 'Status', type: 'text', get: r => r.editable ? 'Editable' : 'Locked' },
];

const accent = '#1A4F9C'; // Professional Navy/Royal Blue matching app theme
const accentGreen = '#006a4e';

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const fmtDMY = s => {
  if (!s) return '—';
  const str = String(s).trim().slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : str;
};
const num = v => { const n = Number(String(v).replace(/,/g, '')); return isNaN(n) ? 0 : n; };
const fmt = n => (Number(n) || 0).toLocaleString('en-IN');
const NoHeader = () => null;

export function DailyProductionScreen({ factory: initialFactoryName, factoryId: initialFactoryId, Header, onBack, isFactoryUser, initialMode }) {
  const [factories, setFactories] = useState([]);
  const [selectedFactoryId, setSelectedFactoryId] = useState(initialFactoryId || '');
  const [orders, setOrders]   = useState(null);
  const [entries, setEntries] = useState(null);
  const [lineRows, setLineRows] = useState(null);
  const [viewMode, setViewMode] = useState('lines'); // 'lines' (table grid) or 'entries' (cards)
  
  // Clean per-column filter state
  const [colFilters, setColFilters] = useState({
    entryno: '',
    entry_dt: '',
    factory: '',
    tasno: '',
    style_name: '',
    shade: '',
    status: ''
  });
  
  const [openId, setOpenId]   = useState(() => (initialMode === 'new' || isFactoryUser ? 'new' : null));
  const [values, setValues]   = useState({});
  const [editable, setEditable] = useState(true);
  const [msg, setMsg]         = useState('');
  const [busy, setBusy]       = useState(false);
  const [entryDt, setEntryDt] = useState(today());
  const [pickedTas, setPickedTas] = useState(new Set());
  const [tasSearch, setTasSearch] = useState('');
  const [showTraining, setShowTraining] = useState(false);

  useEffect(() => {
    api('/production/factories')
      .then(d => setFactories(Array.isArray(d) ? d : []))
      .catch(() => setFactories([]));
  }, []);

  const activeFactoryId = selectedFactoryId || initialFactoryId || '';
  const fidQs = activeFactoryId ? `?factoryid=${encodeURIComponent(activeFactoryId)}` : '';

  const load = () => {
    if (activeFactoryId) {
      api('/production/orders' + fidQs).then(d => setOrders(Array.isArray(d) ? d : [])).catch(() => setOrders([]));
    } else {
      setOrders([]);
    }

    api('/production/entries' + fidQs)
      .then(d => setEntries(Array.isArray(d) ? d : []))
      .catch(() => setEntries([]));

    api('/production/entries-lines' + fidQs + (fidQs ? '&limit=300' : '?limit=300'))
      .then(d => setLineRows(Array.isArray(d) ? d : []))
      .catch(() => setLineRows([]));
  };

  useEffect(load, [activeFactoryId]);

  const startNew = () => {
    if (!activeFactoryId) {
      setMsg('Please select a Factory first before creating an entry.');
      return;
    }
    setOpenId('new'); setValues({}); setEditable(true); setMsg(''); setEntryDt(today());
    setPickedTas(new Set()); setTasSearch('');
  };

  const openEntry = async (id) => {
    setBusy(true); setMsg('');
    try {
      const d = await api('/production/entry/' + id + (activeFactoryId ? `?factoryid=${encodeURIComponent(activeFactoryId)}` : ''));
      setOpenId(id);
      setEditable(!!d.editable);
      setEntryDt(String(d.header.entry_dt).slice(0, 10));
      const map = {}; const tasSet = new Set();
      for (const l of (d.lines || [])) {
        map[l.refid] = {
          qty: l.qty,
          cuts: l.cuts, prodiss: l.prodiss, prod: l.prod, tready: l.tready,
          total_cutting: l.total_cutting, total_input: l.total_input,
          total_output: l.total_output,  total_ready: l.total_ready,
          tasno: l.tasno, designid: l.designid, shadeid: l.shadeid,
          style_name: l.style_name, shade: l.shade,
        };
        if (l.tasno) tasSet.add(l.tasno);
      }
      setValues(map);
      setPickedTas(tasSet); setTasSearch('');
    } catch (e) { setMsg(e.message); }
    finally { setBusy(false); }
  };

  const closeForm = () => { setOpenId(null); setValues({}); setMsg(''); };

  const rowsToSave = useMemo(() => {
    if (!orders) return [];
    if (openId === 'new') {
      return orders.filter(o => {
        const v = values[o.refid];
        return v && (num(v.cuts) || num(v.prodiss) || num(v.prod) || num(v.tready));
      }).map(o => ({
        refid: o.refid, tasno: o.tas_no, designid: o.style_nameid, shadeid: o.shadeid,
        qty: o.order_qty, cuts: num(values[o.refid].cuts), prodiss: num(values[o.refid].prodiss),
        prod: num(values[o.refid].prod), tready: num(values[o.refid].tready),
      }));
    }
    return Object.entries(values).map(([refid, v]) => ({
      refid, tasno: v.tasno, designid: v.designid, shadeid: v.shadeid, qty: v.qty,
      cuts: num(v.cuts), prodiss: num(v.prodiss), prod: num(v.prod), tready: num(v.tready),
    }));
  }, [openId, orders, values]);

  const save = async () => {
    if (!rowsToSave.length) { setMsg('Enter production against at least one order before saving.'); return; }
    setBusy(true); setMsg('');
    try {
      const url = (openId === 'new' ? '/production/entry' : '/production/entry/' + openId) + (activeFactoryId ? `?factoryid=${encodeURIComponent(activeFactoryId)}` : '');
      const body = { entry_dt: entryDt, lines: rowsToSave, ...(activeFactoryId ? { factoryid: activeFactoryId } : {}) };
      const r = await fetch('/api' + url, {
        method: openId === 'new' ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + localStorage.getItem('dj_token') },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error((await r.json()).error || 'Save failed');
      const out = await r.json();
      setMsg('Saved · Entry #' + (out.entryno || out.id));
      closeForm();
      load();
    } catch (e) { setMsg(e.message); }
    finally { setBusy(false); }
  };

  // Filtered detailed lines
  const filteredLines = useMemo(() => {
    if (!lineRows) return [];
    return lineRows.filter(r => {
      if (colFilters.entryno && !String(r.entryno || '').toLowerCase().includes(colFilters.entryno.toLowerCase())) return false;
      if (colFilters.entry_dt && !String(r.entry_dt_str || '').toLowerCase().includes(colFilters.entry_dt.toLowerCase())) return false;
      if (colFilters.factory && !String(r.factory || '').toLowerCase().includes(colFilters.factory.toLowerCase())) return false;
      if (colFilters.tasno && !String(r.tasno || '').toLowerCase().includes(colFilters.tasno.toLowerCase())) return false;
      if (colFilters.style_name && !String(r.style_name || '').toLowerCase().includes(colFilters.style_name.toLowerCase())) return false;
      if (colFilters.shade && !String(r.shade || '').toLowerCase().includes(colFilters.shade.toLowerCase())) return false;
      if (colFilters.status) {
        const isEditable = !!r.editable;
        if (colFilters.status === 'editable' && !isEditable) return false;
        if (colFilters.status === 'locked' && isEditable) return false;
      }
      return true;
    });
  }, [lineRows, colFilters]);

  // Totals of visible filtered lines
  const totals = useMemo(() => {
    let order = 0, cuts = 0, prodiss = 0, prod = 0, tready = 0;
    for (const r of filteredLines) {
      order += Number(r.order_qty) || 0;
      cuts += Number(r.cuts) || 0;
      prodiss += Number(r.prodiss) || 0;
      prod += Number(r.prod) || 0;
      tready += Number(r.tready) || 0;
    }
    return { order, cuts, prodiss, prod, tready };
  }, [filteredLines]);

  const hasAnyFilter = Object.values(colFilters).some(Boolean);
  const clearColFilters = () => setColFilters({ entryno: '', entry_dt: '', factory: '', tasno: '', style_name: '', shade: '', status: '' });

  const currentFactoryName = factories.find(f => String(f.factoryid) === String(activeFactoryId))?.factory_name || initialFactoryName || '';

  return (
    <div className="screen" style={{ width: '100%', maxWidth: '100%', background: '#f8fafc', minHeight: '100vh' }}>
      {Header && <Header subtitle="FACTORY · DAILY PRODUCTION ENTRY" />}
      {openId === null && (
        <ExportBar
          reportKey="daily-production"
          videoStage={0}
          title={`${currentFactoryName || 'Factory'} - Daily Production (${viewMode === 'lines' ? 'Details' : 'Entries'})`}
          sections={[{
            name: viewMode === 'lines' ? 'Production Details' : 'Production Entries',
            columns: viewMode === 'lines' ? LINE_COLS : ENTRY_COLS,
            rows: viewMode === 'lines' ? (filteredLines || []) : (entries || [])
          }]}
          source={{ endpoint: '/production/' + (viewMode === 'lines' ? 'entries-lines' : 'entries') + fidQs }}
          factory={currentFactoryName}
        />
      )}
      
      {/* Top Banner / Breadcrumb */}
      <div style={{
        padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 14,
        background: '#ffffff', borderBottom: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
        flexWrap: 'wrap'
      }}>
        {onBack && (
          <button onClick={onBack} style={{ background: 'none', border: 'none', color: accent, fontSize: 20, cursor: 'pointer', padding: 0 }}>
            ←
          </button>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8, background: '#eef2ff',
            color: accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16
          }}>
            📋
          </div>
          <div>
            <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 17, color: '#0f172a' }}>
              Daily Production Entry
              {currentFactoryName && <span style={{ color: '#64748b', fontWeight: 500, fontSize: 15 }}> · {currentFactoryName}</span>}
            </div>
            <div style={{ fontSize: 11.5, color: '#64748b' }}>
              Form 5582 · Real-time cutting, sewing input, production output & ready tracking
            </div>
          </div>
        </div>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => setShowTraining(true)}
            style={{
              background: 'linear-gradient(135deg, #1A4F9C 0%, #0d2b5c 100%)',
              color: '#ffffff',
              border: 'none',
              padding: '6px 14px',
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 700,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              boxShadow: '0 2px 4px rgba(26,79,156,0.2)'
            }}
            title="Watch Step-by-Step Video Guide for Daily Production Entry (English & Hindi)"
          >
            <span>🎥</span>
            <span>VIDEO GUIDE</span>
          </button>
          {openId === null && (
            <button
              onClick={startNew}
              style={{
                background: accentGreen, color: '#ffffff', border: 'none', borderRadius: 6,
                padding: '7px 18px', cursor: 'pointer', fontSize: 12, fontWeight: 700,
                display: 'flex', alignItems: 'center', gap: 6, boxShadow: '0 2px 4px rgba(0,106,78,0.2)'
              }}
            >
              <span>+</span>
              <span>New Entry</span>
            </button>
          )}
        </div>
      </div>

      {msg && (
        <div style={{
          padding: '10px 20px',
          background: msg.startsWith('Saved') ? '#ecfdf5' : '#fef2f2',
          color: msg.startsWith('Saved') ? '#047857' : '#b91c1c',
          fontSize: 13, fontWeight: 600, borderBottom: '1px solid #e2e8f0',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <span>{msg}</span>
          <button onClick={() => setMsg('')} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 16 }}>✕</button>
        </div>
      )}

      {/* ===================== LIST VIEW ===================== */}
      {openId === null && (
        <div style={{ padding: '16px 20px 40px' }}>
          {/* Controls Bar: Clean Factory Selector, View Toggle, Quick Stats */}
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center',
            justifyContent: 'space-between', marginBottom: 12,
            background: '#ffffff', padding: '10px 16px', borderRadius: 8,
            border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
          }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>Factory:</span>
                <select
                  value={selectedFactoryId}
                  onChange={e => setSelectedFactoryId(e.target.value)}
                  style={{
                    padding: '6px 12px', borderRadius: 6, border: '1px solid #cbd5e1',
                    background: '#ffffff', color: '#0f172a', fontSize: 12.5, fontWeight: 600,
                    cursor: 'pointer', outline: 'none'
                  }}
                >
                  <option value="">-- All Factories --</option>
                  {factories.map(f => (
                    <option key={f.factoryid} value={f.factoryid}>{f.factory_name}</option>
                  ))}
                </select>
              </div>

              {hasAnyFilter && (
                <button
                  onClick={clearColFilters}
                  style={{
                    background: '#fff7ed', color: '#c2410c', border: '1px solid #ffedd5',
                    borderRadius: 6, padding: '5px 12px', fontSize: 11.5, fontWeight: 600,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4
                  }}
                >
                  <span>✕</span>
                  <span>Clear Filters</span>
                </button>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <span style={{ fontSize: 12, color: '#64748b' }}>
                Showing <b style={{ color: '#0f172a' }}>{filteredLines.length}</b> lines
              </span>

              {/* View Mode Switcher */}
              <div style={{ display: 'flex', background: '#f1f5f9', padding: 2, borderRadius: 6 }}>
                <button
                  onClick={() => setViewMode('lines')}
                  style={{
                    padding: '5px 12px', border: 'none', borderRadius: 4, cursor: 'pointer',
                    fontSize: 11.5, fontWeight: 600,
                    background: viewMode === 'lines' ? '#ffffff' : 'transparent',
                    color: viewMode === 'lines' ? '#0f172a' : '#64748b',
                    boxShadow: viewMode === 'lines' ? '0 1px 2px rgba(0,0,0,0.06)' : 'none'
                  }}
                >
                  Detailed Grid
                </button>
                <button
                  onClick={() => setViewMode('entries')}
                  style={{
                    padding: '5px 12px', border: 'none', borderRadius: 4, cursor: 'pointer',
                    fontSize: 11.5, fontWeight: 600,
                    background: viewMode === 'entries' ? '#ffffff' : 'transparent',
                    color: viewMode === 'entries' ? '#0f172a' : '#64748b',
                    boxShadow: viewMode === 'entries' ? '0 1px 2px rgba(0,0,0,0.06)' : 'none'
                  }}
                >
                  Entry Cards
                </button>
              </div>
            </div>
          </div>

          {/* MODE 1: Detailed Table View */}
          {viewMode === 'lines' && (
            <div style={{
              background: '#ffffff', borderRadius: 8, border: '1px solid #e2e8f0',
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)', overflow: 'hidden'
            }}>
              <div style={{ overflowX: 'auto', maxHeight: '72vh' }}>
                <table style={{ width: '100%', minWidth: 1260, borderCollapse: 'separate', borderSpacing: 0, fontSize: 12 }}>
                  <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                    {/* Header Row */}
                    <tr style={{ background: '#f8fafc', color: '#475569', borderBottom: '1px solid #e2e8f0' }}>
                      <th style={{ ...thHdr, width: 70, textAlign: 'center' }}>Action</th>
                      <th style={thHdr}>Entry No</th>
                      <th style={thHdr}>Date</th>
                      <th style={thHdr}>Factory</th>
                      <th style={thHdr}>TAS No</th>
                      <th style={thHdr}>Style</th>
                      <th style={thHdr}>Shade</th>
                      <th style={{ ...thHdr, textAlign: 'right' }}>Order Qty</th>
                      <th style={{ ...thHdr, textAlign: 'right', background: '#f1f5f9' }}>Today's Cut</th>
                      <th style={{ ...thHdr, textAlign: 'right' }}>Total Cut</th>
                      <th style={{ ...thHdr, textAlign: 'right', background: '#f1f5f9' }}>Today Issue</th>
                      <th style={{ ...thHdr, textAlign: 'right' }}>Total Input</th>
                      <th style={{ ...thHdr, textAlign: 'right', background: '#f1f5f9' }}>Today Prod</th>
                      <th style={{ ...thHdr, textAlign: 'right' }}>Total Output</th>
                      <th style={{ ...thHdr, textAlign: 'right', background: '#f1f5f9' }}>Today Ready</th>
                      <th style={{ ...thHdr, textAlign: 'right' }}>Ready Qty</th>
                      <th style={{ ...thHdr, width: 85, textAlign: 'center' }}>Status</th>
                    </tr>

                    {/* Filter Inputs Row */}
                    <tr style={{ background: '#f1f5f9', borderBottom: '2px solid #cbd5e1' }}>
                      <th style={{ padding: '4px 6px', textAlign: 'center', color: '#94a3b8' }}>🔍</th>
                      <th style={{ padding: '4px 6px' }}>
                        <input
                          placeholder="Filter #"
                          value={colFilters.entryno}
                          onChange={e => setColFilters(s => ({ ...s, entryno: e.target.value }))}
                          style={filterInputStyle}
                        />
                      </th>
                      <th style={{ padding: '4px 6px' }}>
                        <input
                          placeholder="DD/MM/YYYY"
                          value={colFilters.entry_dt}
                          onChange={e => setColFilters(s => ({ ...s, entry_dt: e.target.value }))}
                          style={filterInputStyle}
                        />
                      </th>
                      <th style={{ padding: '4px 6px' }}>
                        <input
                          placeholder="Factory…"
                          value={colFilters.factory}
                          onChange={e => setColFilters(s => ({ ...s, factory: e.target.value }))}
                          style={filterInputStyle}
                        />
                      </th>
                      <th style={{ padding: '4px 6px' }}>
                        <input
                          placeholder="TAS…"
                          value={colFilters.tasno}
                          onChange={e => setColFilters(s => ({ ...s, tasno: e.target.value }))}
                          style={filterInputStyle}
                        />
                      </th>
                      <th style={{ padding: '4px 6px' }}>
                        <input
                          placeholder="Style…"
                          value={colFilters.style_name}
                          onChange={e => setColFilters(s => ({ ...s, style_name: e.target.value }))}
                          style={filterInputStyle}
                        />
                      </th>
                      <th style={{ padding: '4px 6px' }}>
                        <input
                          placeholder="Shade…"
                          value={colFilters.shade}
                          onChange={e => setColFilters(s => ({ ...s, shade: e.target.value }))}
                          style={filterInputStyle}
                        />
                      </th>
                      <th style={{ padding: '4px 6px', textAlign: 'right', color: '#94a3b8', fontSize: 11 }}>—</th>
                      <th style={{ padding: '4px 6px', textAlign: 'right', color: '#94a3b8', fontSize: 11, background: '#e2e8f0' }}>—</th>
                      <th style={{ padding: '4px 6px', textAlign: 'right', color: '#94a3b8', fontSize: 11 }}>—</th>
                      <th style={{ padding: '4px 6px', textAlign: 'right', color: '#94a3b8', fontSize: 11, background: '#e2e8f0' }}>—</th>
                      <th style={{ padding: '4px 6px', textAlign: 'right', color: '#94a3b8', fontSize: 11 }}>—</th>
                      <th style={{ padding: '4px 6px', textAlign: 'right', color: '#94a3b8', fontSize: 11, background: '#e2e8f0' }}>—</th>
                      <th style={{ padding: '4px 6px', textAlign: 'right', color: '#94a3b8', fontSize: 11 }}>—</th>
                      <th style={{ padding: '4px 6px', textAlign: 'right', color: '#94a3b8', fontSize: 11, background: '#e2e8f0' }}>—</th>
                      <th style={{ padding: '4px 6px', textAlign: 'right', color: '#94a3b8', fontSize: 11 }}>—</th>
                      <th style={{ padding: '4px 6px' }}>
                        <select
                          value={colFilters.status}
                          onChange={e => setColFilters(s => ({ ...s, status: e.target.value }))}
                          style={{ ...filterInputStyle, padding: '3px 4px' }}
                        >
                          <option value="">All</option>
                          <option value="editable">Editable</option>
                          <option value="locked">Locked</option>
                        </select>
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredLines.length === 0 && (
                      <tr>
                        <td colSpan={17} style={{ padding: '40px 20px', textAlign: 'center', color: '#64748b' }}>
                          {lineRows === null ? 'Loading entries…' : 'No records match the current filters.'}
                        </td>
                      </tr>
                    )}
                    {filteredLines.map((r, idx) => {
                      const isEditable = !!r.editable;
                      const zebra = idx % 2 === 0 ? '#ffffff' : '#f8fafc';
                      return (
                        <tr
                          key={r.line_id}
                          onClick={() => openEntry(r.entry_id)}
                          style={{
                            background: zebra,
                            borderBottom: '1px solid #f1f5f9',
                            cursor: 'pointer',
                            transition: 'background 0.12s ease'
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = '#edf5ff'}
                          onMouseLeave={e => e.currentTarget.style.background = zebra}
                        >
                          {/* Action Button */}
                          <td style={{ ...tdCell, textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                            <button
                              onClick={() => openEntry(r.entry_id)}
                              style={{
                                background: isEditable ? accentGreen : '#f1f5f9',
                                color: isEditable ? '#ffffff' : '#64748b',
                                border: isEditable ? 'none' : '1px solid #cbd5e1',
                                padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700,
                                cursor: 'pointer'
                              }}
                            >
                              {isEditable ? 'Edit' : 'View'}
                            </button>
                          </td>

                          <td style={{ ...tdCell, fontWeight: 700, color: '#1e293b' }}>#{r.entryno}</td>
                          <td style={tdCell}>{r.entry_dt_str}</td>
                          <td style={{ ...tdCell, fontWeight: 600, color: '#334155' }}>{r.factory || '—'}</td>
                          <td style={{ ...tdCell, fontWeight: 700, color: '#1A4F9C' }}>{r.tasno}</td>
                          <td style={tdCell}>{r.style_name || '—'}</td>
                          <td style={{ ...tdCell, color: '#475569' }}>{r.shade || '—'}</td>
                          <td style={{ ...tdCell, textAlign: 'right', fontWeight: 600 }}>{fmt(r.order_qty)}</td>

                          {/* Activity Columns with subtle highlight on non-zero */}
                          <td style={{ ...tdCell, textAlign: 'right', background: '#fafcff', fontWeight: r.cuts > 0 ? 700 : 400, color: r.cuts > 0 ? '#059669' : 'inherit' }}>
                            {fmt(r.cuts)}
                          </td>
                          <td style={{ ...tdCell, textAlign: 'right', color: '#64748b' }}>{fmt(r.tcuts)}</td>

                          <td style={{ ...tdCell, textAlign: 'right', background: '#fafcff', fontWeight: r.prodiss > 0 ? 700 : 400, color: r.prodiss > 0 ? '#059669' : 'inherit' }}>
                            {fmt(r.prodiss)}
                          </td>
                          <td style={{ ...tdCell, textAlign: 'right', color: '#64748b' }}>{fmt(r.input)}</td>

                          <td style={{ ...tdCell, textAlign: 'right', background: '#fafcff', fontWeight: r.prod > 0 ? 700 : 400, color: r.prod > 0 ? '#059669' : 'inherit' }}>
                            {fmt(r.prod)}
                          </td>
                          <td style={{ ...tdCell, textAlign: 'right', color: '#64748b' }}>{fmt(r.output)}</td>

                          <td style={{ ...tdCell, textAlign: 'right', background: '#fafcff', fontWeight: r.tready > 0 ? 700 : 400, color: r.tready > 0 ? '#059669' : 'inherit' }}>
                            {fmt(r.tready)}
                          </td>
                          <td style={{ ...tdCell, textAlign: 'right', color: '#64748b' }}>{fmt(r.ready)}</td>

                          {/* Status Badge */}
                          <td style={{ ...tdCell, textAlign: 'center' }}>
                            <span style={{
                              padding: '2px 8px', borderRadius: 12, fontSize: 10.5, fontWeight: 700,
                              background: isEditable ? '#ecfdf5' : '#f1f5f9',
                              color: isEditable ? '#047857' : '#64748b',
                              border: `1px solid ${isEditable ? '#a7f3d0' : '#e2e8f0'}`
                            }}>
                              {isEditable ? 'Editable' : 'Locked'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>

                  {/* Summary Footer */}
                  {filteredLines.length > 0 && (
                    <tfoot style={{ position: 'sticky', bottom: 0, zIndex: 5, background: '#f8fafc', borderTop: '2px solid #cbd5e1' }}>
                      <tr style={{ fontWeight: 700, color: '#0f172a' }}>
                        <td style={{ ...tdCell, textAlign: 'center' }}>TOTAL</td>
                        <td colSpan={6} style={{ ...tdCell, color: '#64748b' }}>{filteredLines.length} line items</td>
                        <td style={{ ...tdCell, textAlign: 'right' }}>{fmt(totals.order)}</td>
                        <td style={{ ...tdCell, textAlign: 'right', color: '#059669', background: '#f1f5f9' }}>{fmt(totals.cuts)}</td>
                        <td style={{ ...tdCell, textAlign: 'right', color: '#64748b' }}>—</td>
                        <td style={{ ...tdCell, textAlign: 'right', color: '#059669', background: '#f1f5f9' }}>{fmt(totals.prodiss)}</td>
                        <td style={{ ...tdCell, textAlign: 'right', color: '#64748b' }}>—</td>
                        <td style={{ ...tdCell, textAlign: 'right', color: '#059669', background: '#f1f5f9' }}>{fmt(totals.prod)}</td>
                        <td style={{ ...tdCell, textAlign: 'right', color: '#64748b' }}>—</td>
                        <td style={{ ...tdCell, textAlign: 'right', color: '#059669', background: '#f1f5f9' }}>{fmt(totals.tready)}</td>
                        <td style={{ ...tdCell, textAlign: 'right', color: '#64748b' }}>—</td>
                        <td style={tdCell}></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          )}

          {/* MODE 2: Card Entries View */}
          {viewMode === 'entries' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: 12 }}>
              {entries && entries.map(e => {
                const isEditable = !!e.editable;
                return (
                  <div
                    key={e.id}
                    onClick={() => openEntry(e.id)}
                    style={{
                      background: '#ffffff', borderRadius: 8, padding: 14,
                      border: '1px solid #e2e8f0', cursor: 'pointer',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.03)', transition: 'transform 0.15s, box-shadow 0.15s'
                    }}
                    onMouseEnter={el => { el.currentTarget.style.transform = 'translateY(-1px)'; el.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.06)'; }}
                    onMouseLeave={el => { el.currentTarget.style.transform = 'none'; el.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.03)'; }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <span style={{ fontWeight: 800, fontSize: 14, color: '#0f172a' }}>Entry #{e.entryno}</span>
                      <span style={{
                        padding: '2px 8px', borderRadius: 10, fontSize: 10.5, fontWeight: 700,
                        background: isEditable ? '#ecfdf5' : '#f1f5f9',
                        color: isEditable ? '#047857' : '#64748b',
                        border: `1px solid ${isEditable ? '#a7f3d0' : '#e2e8f0'}`
                      }}>
                        {isEditable ? 'Editable' : 'Locked'}
                      </span>
                    </div>

                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6 }}>
                      🏭 <b>{e.factory_name || 'Factory'}</b> · 📅 {fmtDMY(e.entry_dt)}
                    </div>
                    <div style={{ fontSize: 12, color: '#334155', fontWeight: 600, marginBottom: 10 }}>
                      TAS: {e.tas_list || '—'}
                    </div>

                    <div style={{
                      display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6,
                      background: '#f8fafc', padding: 8, borderRadius: 6, textAlign: 'center'
                    }}>
                      <div>
                        <div style={{ fontSize: 9.5, color: '#64748b', textTransform: 'uppercase' }}>Cuts</div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{fmt(e.total_cuts)}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 9.5, color: '#64748b', textTransform: 'uppercase' }}>Issue</div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{fmt(e.total_prodiss)}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 9.5, color: '#64748b', textTransform: 'uppercase' }}>Prod</div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{fmt(e.total_prod)}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 9.5, color: '#64748b', textTransform: 'uppercase' }}>Ready</div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{fmt(e.total_tready)}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ===================== FORM VIEW ===================== */}
      {openId !== null && (() => {
        const tasCatalog = openId === 'new'
          ? [...new Set((orders || []).map(o => o.tas_no))].filter(Boolean)
          : [...new Set(Object.values(values).map(v => v.tasno))].filter(Boolean);
        const q = tasSearch.trim().toLowerCase();
        const shownTas = q ? tasCatalog.filter(t => String(t).toLowerCase().includes(q)) : tasCatalog;
        const toggleTas = t => setPickedTas(s => { const n = new Set(s); n.has(t) ? n.delete(t) : n.add(t); return n; });
        const gridRows = openId === 'new'
          ? (orders || []).filter(o => pickedTas.has(o.tas_no))
          : Object.entries(values).filter(([, v]) => pickedTas.has(v.tasno)).map(([rid, v]) => ({
              refid: rid, tas_no: v.tasno, style_name: v.style_name, shade: v.shade,
              order_qty: v.qty, total_cutting: v.total_cutting, total_input: v.total_input,
              total_output: v.total_output, total_ready: v.total_ready,
              style_nameid: v.designid, shadeid: v.shadeid,
            }));

        return (
          <div style={{ padding: '16px 20px 40px' }}>
            {/* Top Toolbar */}
            <div style={{
              display: 'flex', gap: 12, alignItems: 'center', marginBottom: 14,
              background: '#ffffff', padding: '12px 16px', borderRadius: 8,
              border: '1px solid #e2e8f0', flexWrap: 'wrap'
            }}>
              <button
                onClick={closeForm}
                style={{
                  background: '#f1f5f9', border: '1px solid #cbd5e1', color: '#334155',
                  padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer'
                }}
              >
                ← Back to List
              </button>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#475569', fontWeight: 600 }}>
                <span>Entry Date:</span>
                {openId === 'new' ? (
                  <input
                    type="date"
                    value={entryDt}
                    onChange={e => setEntryDt(e.target.value)}
                    style={{
                      padding: '5px 8px', borderRadius: 6, border: '1px solid #cbd5e1',
                      background: '#ffffff', color: '#0f172a', fontSize: 12, fontWeight: 600
                    }}
                  />
                ) : (
                  <b style={{ color: '#0f172a' }}>{fmtDMY(entryDt)}</b>
                )}
              </div>

              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{
                  padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                  background: editable ? '#ecfdf5' : '#f1f5f9',
                  color: editable ? '#047857' : '#64748b',
                  border: `1px solid ${editable ? '#a7f3d0' : '#e2e8f0'}`
                }}>
                  {editable ? '● Active Today (Editable)' : '🔒 Read-only (Locked)'}
                </span>
              </div>
            </div>

            {/* TAS Picker for New Entry */}
            {openId === 'new' && (
              <div style={{
                background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 8,
                padding: 14, marginBottom: 14, boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
              }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#475569', letterSpacing: 0.5, textTransform: 'uppercase' }}>Select TAS Orders:</span>
                  <input
                    placeholder="Search TAS number…"
                    value={tasSearch}
                    onChange={e => setTasSearch(e.target.value)}
                    style={{
                      flex: 1, minWidth: 180, padding: '5px 10px', borderRadius: 6,
                      border: '1px solid #cbd5e1', fontSize: 12
                    }}
                  />
                  <button type="button" onClick={() => setPickedTas(new Set(shownTas))} style={btnPill}>Select All</button>
                  <button type="button" onClick={() => setPickedTas(new Set())} style={btnPill}>Clear</button>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 120, overflowY: 'auto' }}>
                  {shownTas.length === 0 && <div style={{ fontSize: 12, color: '#64748b', padding: 8 }}>{orders === null ? 'Loading TAS orders…' : 'No TAS orders match search.'}</div>}
                  {shownTas.map(t => {
                    const on = pickedTas.has(t);
                    return (
                      <button
                        key={t}
                        type="button"
                        onClick={() => toggleTas(t)}
                        style={{
                          padding: '4px 12px', borderRadius: 16, fontSize: 11.5, fontWeight: 600, cursor: 'pointer',
                          background: on ? accent : '#f1f5f9',
                          color: on ? '#ffffff' : '#334155',
                          border: `1px solid ${on ? accent : '#cbd5e1'}`
                        }}
                      >
                        {t}
                      </button>
                    );
                  })}
                </div>
                <div style={{ marginTop: 8, fontSize: 11, color: '#64748b' }}>
                  <b>{pickedTas.size}</b> of {tasCatalog.length} selected · <b>{gridRows.length}</b> shade rows
                </div>
              </div>
            )}

            {/* Entry Edit Grid */}
            <div style={{
              background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 8,
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)', overflow: 'hidden'
            }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', minWidth: 950, borderCollapse: 'separate', borderSpacing: 0, fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', color: '#475569', borderBottom: '1px solid #e2e8f0' }}>
                      <th style={thHdr}>TAS</th>
                      <th style={thHdr}>Style</th>
                      <th style={thHdr}>Shade</th>
                      <th style={{ ...thHdr, textAlign: 'right' }}>Order Qty</th>
                      <th style={{ ...thHdr, textAlign: 'right', background: '#fef3c7', color: '#92400e' }}>Today's Cutting</th>
                      <th style={{ ...thHdr, textAlign: 'right', background: '#fef3c7', color: '#92400e' }}>Today Issue</th>
                      <th style={{ ...thHdr, textAlign: 'right', background: '#fef3c7', color: '#92400e' }}>Today Prod</th>
                      <th style={{ ...thHdr, textAlign: 'right', background: '#fef3c7', color: '#92400e' }}>Today Ready</th>
                      <th style={{ ...thHdr, textAlign: 'right' }}>Total Cutting</th>
                      <th style={{ ...thHdr, textAlign: 'right' }}>Total Input</th>
                      <th style={{ ...thHdr, textAlign: 'right' }}>Total Output</th>
                      <th style={{ ...thHdr, textAlign: 'right' }}>Ready Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gridRows.map(o => {
                      const v = values[o.refid] || {};
                      const inp = k => (
                        <input
                          value={v[k] != null ? v[k] : ''}
                          onChange={e => setValues(s => ({
                            ...s,
                            [o.refid]: {
                              ...(s[o.refid] || o),
                              [k]: e.target.value,
                              tasno: o.tas_no,
                              designid: o.style_nameid || o.designid,
                              shadeid: o.shadeid,
                              qty: o.order_qty
                            }
                          }))}
                          disabled={!editable}
                          inputMode="numeric"
                          style={{
                            width: 75, padding: '4px 6px', border: '1px solid #cbd5e1',
                            borderRadius: 4, textAlign: 'right', fontSize: 12, fontWeight: 600,
                            background: editable ? '#ffffff' : '#f8fafc', color: '#0f172a',
                            outline: 'none'
                          }}
                        />
                      );
                      return (
                        <tr key={o.refid} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ ...tdCell, fontWeight: 700, color: '#1A4F9C' }}>{o.tas_no}</td>
                          <td style={tdCell}>{o.style_name || '—'}</td>
                          <td style={tdCell}>{o.shade || '—'}</td>
                          <td style={{ ...tdCell, textAlign: 'right', fontWeight: 600 }}>{fmt(o.order_qty)}</td>
                          <td style={{ ...tdCell, textAlign: 'right', background: '#fffbeb' }}>{inp('cuts')}</td>
                          <td style={{ ...tdCell, textAlign: 'right', background: '#fffbeb' }}>{inp('prodiss')}</td>
                          <td style={{ ...tdCell, textAlign: 'right', background: '#fffbeb' }}>{inp('prod')}</td>
                          <td style={{ ...tdCell, textAlign: 'right', background: '#fffbeb' }}>{inp('tready')}</td>
                          <td style={{ ...tdCell, textAlign: 'right', color: '#64748b' }}>{fmt(o.total_cutting)}</td>
                          <td style={{ ...tdCell, textAlign: 'right', color: '#64748b' }}>{fmt(o.total_input)}</td>
                          <td style={{ ...tdCell, textAlign: 'right', color: '#64748b' }}>{fmt(o.total_output)}</td>
                          <td style={{ ...tdCell, textAlign: 'right', color: '#64748b' }}>{fmt(o.total_ready)}</td>
                        </tr>
                      );
                    })}
                    {gridRows.length === 0 && (
                      <tr>
                        <td colSpan={12} style={{ padding: '30px 20px', textAlign: 'center', color: '#64748b' }}>
                          {orders === null ? 'Loading TAS orders…' : 'Pick one or more TAS above to load order rows.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14 }}>
              <button
                onClick={closeForm}
                style={{
                  background: '#ffffff', border: '1px solid #cbd5e1', color: '#475569',
                  padding: '8px 18px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              {editable && (
                <button
                  onClick={save}
                  disabled={busy || pickedTas.size === 0}
                  style={{
                    background: accentGreen, color: '#ffffff', border: 'none',
                    padding: '8px 24px', borderRadius: 6, fontSize: 12.5, fontWeight: 700,
                    cursor: 'pointer', opacity: pickedTas.size === 0 ? 0.5 : 1,
                    boxShadow: '0 2px 4px rgba(0,106,78,0.2)'
                  }}
                >
                  {busy ? 'Saving…' : (openId === 'new' ? 'Save Entry' : 'Update Entry')}
                </button>
              )}
            </div>
          </div>
        );
      })()}
      {showTraining && <GmtTrainingModal initialStage={2} factory={currentFactoryName} onClose={() => setShowTraining(false)} />}
    </div>
  );
}

export default function Production({ user, onLogout }) {
  const factoryName = (user && (user.factoryName || user.factory_name)) || '';
  const factoryPartyId = (user && (user.factoryPartyId || user.factory_partyid)) || null;

  return (
    <FactoryView
      initialFactory={factoryName}
      initialFactoryId={factoryPartyId}
      isFactoryUser={true}
      user={user}
      onLogout={onLogout}
    />
  );
}

const thHdr = {
  padding: '10px 10px',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.3,
  textAlign: 'left',
  borderBottom: '1px solid #e2e8f0',
  whiteSpace: 'nowrap'
};

const tdCell = {
  padding: '8px 10px',
  borderBottom: '1px solid #f1f5f9',
  whiteSpace: 'nowrap'
};

const filterInputStyle = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '4px 8px',
  borderRadius: 4,
  border: '1px solid #cbd5e1',
  background: '#ffffff',
  color: '#0f172a',
  fontSize: 11,
  outline: 'none'
};

const btnPill = {
  background: '#f1f5f9',
  border: '1px solid #cbd5e1',
  color: '#334155',
  padding: '4px 10px',
  borderRadius: 4,
  fontSize: 11,
  cursor: 'pointer'
};
